/**
 * Noms réservés : aucun nom d'utilisateur ni nom d'entreprise ne peut reprendre
 * la marque MyEquip ou un nom qui laisserait croire à un compte officiel
 * (administration, support, sécurité…).
 *
 * La comparaison se fait sur une forme normalisée : minuscules, sans accents,
 * chiffres et symboles de substitution ramenés à leur lettre (« MyEqu1p »,
 * « my-équip », « MY.EQUIP » donnent tous « myequip »), séparateurs retirés.
 *
 * - `contains` : interdit dès que le terme apparaît (« myequip-support »,
 *   « Plomberie MyEquip ») ;
 * - `exact` : interdit seulement comme nom complet (« admin », mais pas
 *   « adminplomberie »), pour ne pas bloquer des noms légitimes.
 *
 * L'administration MyEquip complète cette liste de base sans déploiement.
 */
export type ReservedMatch = 'exact' | 'contains';

export interface ReservedTerm {
  term: string;
  match: ReservedMatch;
}

const SUBSTITUTIONS: Record<string, string> = {
  '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '8': 'b', '9': 'g',
  '@': 'a', '$': 's', '€': 'e', '!': 'i', '|': 'i',
};

/** Forme de comparaison : lettres et chiffres non substituables uniquement. */
export function normalizeReservedName(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[0-9@$€!|]/g, (char) => SUBSTITUTIONS[char] ?? char)
    .replace(/[^a-z0-9]/g, '');
}

export const BASE_RESERVED_NAMES: readonly ReservedTerm[] = [
  { term: 'myequip', match: 'contains' },
  ...[
    'admin', 'administrateur', 'administrator', 'administration', 'root', 'superadmin',
    'sysadmin', 'support', 'assistance', 'aide', 'help', 'helpdesk', 'contact', 'securite',
    'security', 'systeme', 'system', 'staff', 'equipe', 'team', 'moderateur', 'moderator',
    'officiel', 'official', 'facturation', 'billing', 'abonnement', 'compta', 'api', 'www',
    'mail', 'email', 'postmaster', 'hostmaster', 'webmaster', 'abuse', 'noreply', 'nepasrepondre',
    'service', 'serviceclient', 'info', 'infos', 'null', 'undefined', 'test', 'demo',
  ].map((term): ReservedTerm => ({ term, match: 'exact' })),
];

/**
 * Terme réservé atteint par `value`, ou `undefined`. `extra` ajoute les termes
 * gérés par l'administration (déjà normalisés).
 */
export function findReservedName(
  value: string,
  extra: readonly ReservedTerm[] = [],
): ReservedTerm | undefined {
  const normalized = normalizeReservedName(value);
  if (!normalized) return undefined;
  return [...BASE_RESERVED_NAMES, ...extra].find((reserved) =>
    reserved.match === 'contains'
      ? normalized.includes(reserved.term)
      : normalized === reserved.term);
}
